import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import HealthTreeNode from "./HealthTreeNode";
import BatterySidebarContainer from "../../components/BatterySidebarContainer";
import { ThemeProperties } from "../../../../Theme/ThemeProperties";

const HIERARCHY_ORDER = ["battery", "string", "pack", "module", "cell"];

const filterTreeByVisibleParts = (nodes, visibleParts) => {
    const result = [];
    for (const node of nodes) {
        const isVisible = visibleParts.includes(node.level?.toLowerCase());
        const filteredChildren = filterTreeByVisibleParts(node.children || [], visibleParts);
        if (isVisible) {
            result.push({ ...node, children: filteredChildren });
        } else {
            result.push(...filteredChildren);
        }
    }
    return result;
};

const getAllNodeIds = (nodes) => {
    const ids = [];
    for (const node of nodes) {
        ids.push(node.id);
        ids.push(...getAllNodeIds(node.children || []));
    }
    return ids;
};

const getInitialOpenNodes = (filteredTree, visibleParts) => {
    const sortedVisible = HIERARCHY_ORDER.filter((l) => visibleParts.includes(l));
    const firstLevel = sortedVisible[0];
    const openMap = {};
    const traverse = (nodes) => {
        nodes.forEach((n) => {
            if (n.level?.toLowerCase() === firstLevel) openMap[n.id] = true;
            traverse(n.children || []);
        });
    };
    traverse(filteredTree);
    return openMap;
};

const BatteryHealthSidebarV2 = ({
    batteryStructure = [],
    onChangeNode = () => {},
    visibleBatteryParts = ["string", "pack"],
    maxScrollHeight = "300px",
    title = "Battery System",
    isLoading = false,
}) => {
    const { t } = useTranslation(["digital_twin"]);

    const normalizedVisible = useMemo(
        () => visibleBatteryParts.map((v) => v.toLowerCase()),
        [visibleBatteryParts]
    );

    const filteredTree = useMemo(
        () => filterTreeByVisibleParts(batteryStructure, normalizedVisible),
        [batteryStructure, normalizedVisible]
    );

    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [openNodes, setOpenNodes] = useState(() => ({}));
    const initializedRef = useRef(false);

    // Set initial selection and open state once the tree is available.
    // Intentionally runs only once (guarded by initializedRef) so a late-loading
    // tree doesn't reset selections the user has already made.
    useEffect(() => {
        if (filteredTree.length === 0 || initializedRef.current) return;

        initializedRef.current = true;
        const openMap = getInitialOpenNodes(filteredTree, normalizedVisible);
        setOpenNodes(openMap);
        setSelectedIds(new Set(getAllNodeIds(filteredTree)));
    }, [filteredTree]); // eslint-disable-line react-hooks/exhaustive-deps

    const debouncedOnChange = useCallback(
        debounce((ids) => onChangeNode([...ids]), 300),
        [onChangeNode]
    );

    const handleToggle = useCallback((nodeId, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(nodeId);
            else next.delete(nodeId);
            return next;
        });
    }, []);

    useEffect(() => {
        if (!initializedRef.current) return;
        debouncedOnChange(selectedIds);
        return () => debouncedOnChange.cancel();
    }, [selectedIds, debouncedOnChange]);

    // Only spin while the hierarchy is actually being fetched. An empty tree after the
    // fetch means this battery has no entity at the configured levels — show that
    // instead of an endless loader.
    if (isLoading) {
        return <CartLoader />;
    }

    if (filteredTree.length === 0) {
        return (
            <BatterySidebarContainer title={title}>
                <Typography variant="fw400s12c686868" data-testid="battery-health-sidebar-empty">
                    {t("digital_twin:no_battery_structure_available", "No battery structure available")}
                </Typography>
            </BatterySidebarContainer>
        );
    }

    return (
        <BatterySidebarContainer title={title}>
            <ConfigProvider theme={{ token: { colorPrimary: ThemeProperties.dropDownPurple, colorBorder: "#9F9F9F" } }}>
                <Box sx={{ overflow: "auto", height: maxScrollHeight, pb: 4 }}>
                    {filteredTree.map((node) => (
                        <HealthTreeNode
                            key={node.id}
                            node={node}
                            level={0}
                            openNodes={openNodes}
                            setOpenNodes={setOpenNodes}
                            selectedIds={selectedIds}
                            onToggle={handleToggle}
                        />
                    ))}
                </Box>
            </ConfigProvider>
        </BatterySidebarContainer>
    );
};

export default BatteryHealthSidebarV2;
